import React, { useState, useEffect, useCallback } from 'react';

const gold = '#d4a843';
const border = 'rgba(212,168,67,0.15)';

export default function AdAnalyticsTab({ backendUrl, token }) {
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [byAd, setByAd] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('summary');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const headers = { Authorization: 'Bearer ' + token };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, tl, ad] = await Promise.all([
        fetch(backendUrl + '/api/ad-analytics/summary', { headers }).then(r => r.json()),
        fetch(backendUrl + '/api/ad-analytics/timeline?limit=100', { headers }).then(r => r.json()),
        fetch(backendUrl + '/api/ad-analytics/by-ad', { headers }).then(r => r.json()),
      ]);
      setSummary(s); setTimeline(tl.events || []); setByAd(ad.by_ad || []);
    } catch(e) {}
    setLoading(false);
  }, [backendUrl, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (!autoRefresh) return; const iv = setInterval(fetchAll, 30000); return () => clearInterval(iv); }, [autoRefresh, fetchAll]);

  const kpi = (icon, label, value, sub) => (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid ' + border, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 20 }}>{icon}</div>
      <div style={{ color: gold, fontSize: 20, fontFamily: "'Bebas Neue',sans-serif", marginTop: 4 }}>{value ?? 0}</div>
      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{label}</div>
      {sub && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const evColor = { impression: '#0066cc', click: '#FFD700', complete: '#00cc66', skip: '#ff6600' };
  const evIcon = { impression: 'EYE', click: 'CLICK', complete: 'DONE', skip: 'SKIP' };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ color: gold, fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: 2 }}>AD REVENUE ANALYTICS</div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>Enterprise OTT-grade ad performance tracking</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} /> Auto-refresh
          </label>
          <button onClick={fetchAll} style={{ background: 'rgba(212,168,67,0.15)', color: gold, border: '1px solid ' + border, borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>REFRESH</button>
          <button onClick={() => window.open(backendUrl + '/api/ad-analytics/export-csv', '_blank')} style={{ background: gold, color: '#000', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontFamily: "'Bebas Neue',sans-serif", cursor: 'pointer' }}>EXPORT CSV</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid ' + border }}>
        {['summary','by-ad','timeline'].map(v => (
          <button key={v} onClick={() => setActiveView(v)} style={{ background: 'none', border: 'none', borderBottom: activeView === v ? '2px solid ' + gold : '2px solid transparent', color: activeView === v ? gold : 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: 13, fontWeight: 700, padding: '8px 16px', cursor: 'pointer', textTransform: 'uppercase' }}>
            {v === 'summary' ? 'Summary' : v === 'by-ad' ? 'By Ad' : 'Timeline'}
          </button>
        ))}
      </div>

      {loading ? <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: 60 }}>Loading analytics...</div> : (
        <>
          {activeView === 'summary' && summary && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 12, marginBottom: 24 }}>
                {kpi('INR', 'Total Revenue', 'Rs.' + (summary.total_revenue_inr||0).toFixed(2))}
                {kpi('DAY', "Today's Revenue", 'Rs.' + (summary.today_revenue_inr||0).toFixed(2))}
                {kpi('WK', 'This Week', 'Rs.' + (summary.week_revenue_inr||0).toFixed(2))}
                {kpi('IMP', 'Impressions', summary.total_impressions||0)}
                {kpi('CLK', 'Clicks', summary.total_clicks||0, 'CTR: '+(summary.ctr||0)+'%')}
                {kpi('CPL', 'Completions', summary.total_completes||0, 'Rate: '+(summary.completion_rate||0)+'%')}
                {kpi('CPM', 'Avg eCPM', 'Rs.'+(summary.avg_ecpm||0).toFixed(2), 'per 1000 imp')}
                {/* null means no request events yet - "0%" would read as total
                    fill failure rather than "not measured". */}
                {kpi('FILL', 'Fill Rate', summary.fill_rate == null ? '—' : summary.fill_rate+'%', summary.fill_rate == null ? 'needs request tracking' : '')}
              </div>
              {summary.total_events === 0 && <div style={{ textAlign:'center', color:'rgba(255,255,255,0.3)', padding:40, border:'1px dashed '+border, borderRadius:10 }}>No ad events yet. Events appear as users view ads.</div>}
              {summary.content_stats && Object.keys(summary.content_stats).length > 0 && (
                <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid '+border, borderRadius:10, padding:16 }}>
                  <div style={{ color:gold, fontFamily:"'Bebas Neue',sans-serif", fontSize:14, letterSpacing:2, marginBottom:12 }}>TOP CONTENT BY AD REVENUE</div>
                  {Object.entries(summary.content_stats).slice(0,10).map(([title,stats],i) => (
                    <div key={title} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                      <span style={{ color:'#fff', fontSize:13 }}>#{i+1} {title.substring(0,40)}</span>
                      <span style={{ color:gold, fontSize:13 }}>Rs.{(stats.revenue_inr||0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeView === 'by-ad' && (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {byAd.length === 0 ? <div style={{ textAlign:'center', color:'rgba(255,255,255,0.3)', padding:40, border:'1px dashed '+border, borderRadius:10 }}>No ad data yet.</div>
              : byAd.map((ad,i) => (
                <div key={i} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid '+border, borderRadius:10, padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
                  <div><div style={{ color:'#fff', fontSize:14, fontWeight:600 }}>{ad.title}</div><div style={{ color:'rgba(255,255,255,0.4)', fontSize:11 }}>{ad.type}</div></div>
                  <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
                    <div style={{ textAlign:'center' }}><div style={{ color:gold, fontSize:16, fontFamily:"'Bebas Neue',sans-serif" }}>{ad.impressions}</div><div style={{ color:'rgba(255,255,255,0.4)', fontSize:10 }}>IMPRESSIONS</div></div>
                    <div style={{ textAlign:'center' }}><div style={{ color:'#FFD700', fontSize:16, fontFamily:"'Bebas Neue',sans-serif" }}>{ad.clicks}</div><div style={{ color:'rgba(255,255,255,0.4)', fontSize:10 }}>CLICKS</div></div>
                    <div style={{ textAlign:'center' }}><div style={{ color:'#00cc66', fontSize:16, fontFamily:"'Bebas Neue',sans-serif" }}>{ad.completes}</div><div style={{ color:'rgba(255,255,255,0.4)', fontSize:10 }}>COMPLETES</div></div>
                    <div style={{ textAlign:'center' }}><div style={{ color:gold, fontSize:16, fontFamily:"'Bebas Neue',sans-serif" }}>Rs.{(ad.revenue_inr||0).toFixed(2)}</div><div style={{ color:'rgba(255,255,255,0.4)', fontSize:10 }}>REVENUE</div></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeView === 'timeline' && (
            <div>
              <div style={{ color:'rgba(255,255,255,0.4)', fontSize:12, marginBottom:12 }}>Last {timeline.length} events</div>
              {timeline.length === 0 ? <div style={{ textAlign:'center', color:'rgba(255,255,255,0.3)', padding:40, border:'1px dashed '+border, borderRadius:10 }}>No events yet.</div>
              : <div style={{ fontFamily:'monospace', background:'rgba(0,0,0,0.4)', borderRadius:10, padding:16, border:'1px solid '+border, maxHeight:500, overflowY:'auto' }}>
                {timeline.map((e,i) => {
                  const time = new Date(e.created_at).toLocaleTimeString('en-IN');
                  return (
                    <div key={e.id||i} style={{ display:'flex', gap:12, padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', flexWrap:'wrap' }}>
                      <span style={{ color:'rgba(255,255,255,0.3)', fontSize:11, minWidth:120 }}>{time}</span>
                      <span style={{ color:evColor[e.event_type]||'#fff', fontSize:11, minWidth:80 }}>{evIcon[e.event_type]||e.event_type}</span>
                      <span style={{ color:'rgba(255,255,255,0.6)', fontSize:11, flex:1 }}>{e.campaign_name||'Direct'} - {e.platform} - {e.device_type}</span>
                      <span style={{ color:'#00cc66', fontSize:11 }}>Rs.{(e.revenue_inr||0).toFixed(4)}</span>
                    </div>
                  );
                })}
              </div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
